// src/shared/utils/excel/exportCommonTableToExcel.js

import * as XLSX from "xlsx-js-style";

/**
 * Best-effort extraction of text from column labels (string or simple React nodes).
 * Used only for Excel headers.
 *
 * @param {any} node
 * @returns {string}
 */
function stringifyHeader(node) {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);

  const props = node?.props;
  const children = props?.children;

  if (Array.isArray(children)) {
    return children.map(stringifyHeader).join(" ").replace(/\s+/g, " ").trim();
  }
  if (children != null) {
    return stringifyHeader(children).replace(/\s+/g, " ").trim();
  }

  return "";
}

/**
 * Convert a CSS width like "120px" to an Excel column width (approx chars).
 * Excel width is roughly: chars ~ px / 7 (good enough for matching your table).
 *
 * @param {any} w
 * @param {number} fallback
 * @returns {number}
 */
function pxToExcelWidth(w, fallback = 14) {
  const s = String(w || "");
  const m = s.match(/(\d+)\s*px/i);
  if (!m) return fallback;
  const px = Number(m[1]);
  if (!Number.isFinite(px) || px <= 0) return fallback;
  return Math.max(8, Math.round(px / 7));
}

/**
 * Safely infer an Excel number format based on CommonTable column metadata.
 *
 * @param {object} col
 * @returns {"currency"|"percent"|"number"|"text"}
 */
function inferFormat(col) {
  const fmt = col?.format;
  if (fmt === "currency") return "currency";
  if (fmt === "percent") return "percent";
  // Default: numbers for non-label cols, otherwise text
  return col?.key === "label" ? "text" : "number";
}

/**
 * Build and download an Excel workbook for a CommonTable.
 *
 * - Exports section header rows (`row.section`) as a bold full-width row.
 * - Exports data rows with per-column formatting (currency/percent/number/text).
 * - Optionally appends aggregateTotal row (if you pass it).
 *
 * @param {object} args
 * @param {Array<object>} args.columns CommonTable columns
 * @param {Array<object>} args.rows Rows to export (typically CommonTable's sortedData)
 * @param {object|null} [args.aggregateRow] Optional aggregate row appended at end
 * @param {string} [args.filename] .xlsx filename
 * @param {string} [args.sheetName]
 * @param {string|null} [args.title] Optional title row (merged visually by content only)
 */
export function exportCommonTableToExcel({
  columns,
  rows,
  aggregateRow = null,
  filename = "table-export.xlsx",
  sheetName = "Table",
  title = null,
}) {
  const exportableCols = (columns || []).filter((c) => c?.key);
  if (!exportableCols.length) return;

  const headers = exportableCols.map((c) =>
    stringifyHeader(c?.label ?? c?.key ?? "")
  );

  const safeRows = Array.isArray(rows) ? rows : [];
  const finalRows = aggregateRow ? [...safeRows, aggregateRow] : safeRows;

  // ---------------------------
  // Styles
  // ---------------------------
  const headerFill = "D5E7F7";
  const border = {
    top: { style: "thin", color: { rgb: "BFBFBF" } },
    bottom: { style: "thin", color: { rgb: "BFBFBF" } },
    left: { style: "thin", color: { rgb: "BFBFBF" } },
    right: { style: "thin", color: { rgb: "BFBFBF" } },
  };

  const headerStyle = {
    font: { bold: true },
    alignment: { vertical: "center", horizontal: "center", wrapText: true },
    fill: { patternType: "solid", fgColor: { rgb: headerFill } },
    border,
  };

  const textStyle = {
    alignment: { vertical: "center", horizontal: "left", wrapText: false },
    border,
  };

  const currencyStyle = {
    alignment: { vertical: "center", horizontal: "right" },
    numFmt: "$#,##0;($#,##0);-",
    border,
  };

  const percentStyle = {
    alignment: { vertical: "center", horizontal: "right" },
    numFmt: "0.0%;-0.0%;-",
    border,
  };

  const numberStyle = {
    alignment: { vertical: "center", horizontal: "right" },
    border,
  };

  const boldRow = (base, fillRgb = "F2F2F2") => ({
    ...base,
    font: { ...(base.font || {}), bold: true },
    fill: { patternType: "solid", fgColor: { rgb: fillRgb } },
  });

  const sectionRowStyle = boldRow(
    { alignment: { vertical: "center", horizontal: "left" }, border },
    "F7F7F7"
  );

  // ---------------------------
  // Build AOA
  // ---------------------------
  const aoa = [];

  if (title) {
    // Title row: label cell has title, rest blank
    aoa.push(exportableCols.map((c) => (c.key === "label" ? title : "")));
  }

  aoa.push(headers);

  for (const r of finalRows) {
    if (r?.section) {
      // Section header row: label cell has section name
      aoa.push(
        exportableCols.map((c) =>
          c.key === "label" ? String(r.section ?? "").trim() : ""
        )
      );
      continue;
    }

    aoa.push(
      exportableCols.map((c) => {
        const key = c.key;
        const v = r?.[key];

        if (key === "label") return r?.label ?? "";

        if (typeof v === "number" && Number.isFinite(v)) return v;

        // allow string exports if a column renders string data
        if (typeof v === "string") return v;

        return "";
      })
    );
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths
  ws["!cols"] = exportableCols.map((c) => {
    const w = c?.style?.width || c?.style?.minWidth;
    return { wch: pxToExcelWidth(w, c.key === "label" ? 40 : 14) };
  });

  // Freeze header row (account for optional title row)
  const headerRowIndex = title ? 1 : 0;
  ws["!freeze"] = { xSplit: 0, ySplit: headerRowIndex + 1 };

  // Style header cells
  for (let c = 0; c < headers.length; c++) {
    const addr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  // Title row styling (if present)
  if (title) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (!ws[addr]) continue;
      ws[addr].s = boldRow(
        { alignment: { vertical: "center", horizontal: "left" } },
        "FFFFFF"
      );
    }
  }

  // Style body rows
  const startRow = headerRowIndex + 1;
  for (let rIdx = startRow; rIdx < aoa.length; rIdx++) {
    // Determine if this is a "section row" by: only label col non-empty and all others empty
    const rowArr = aoa[rIdx];
    const isSectionRow =
      rowArr &&
      String(rowArr[0] ?? "") !== "" &&
      rowArr.slice(1).every((x) => x === "" || x == null);

    // Determine if "total-like" (common in your tables)
    const labelText = String(rowArr?.[0] ?? "").trim().toUpperCase();
    const isTotalRow =
      labelText.startsWith("TOTAL ") ||
      labelText === "AGGREGATE TOTAL" ||
      labelText === "TOTAL";

    for (let c = 0; c < exportableCols.length; c++) {
      const col = exportableCols[c];
      const addr = XLSX.utils.encode_cell({ r: rIdx, c });
      const cell = ws[addr];
      if (!cell) continue;

      if (isSectionRow) {
        cell.s = sectionRowStyle;
        continue;
      }

      const fmt = inferFormat(col);
      let base = textStyle;

      if (col.key !== "label") {
        if (fmt === "currency") base = currencyStyle;
        else if (fmt === "percent") base = percentStyle;
        else if (fmt === "number") base = numberStyle;
        else base = textStyle;
      }

      cell.s = isTotalRow ? boldRow(base, "F2F2F2") : base;
    }
  }

  // Build workbook + download
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const safeName = String(filename || "table-export.xlsx").endsWith(".xlsx")
    ? filename
    : `${filename}.xlsx`;

  XLSX.writeFile(wb, safeName, { compression: true });
}
